/**
 * Run-orkestratie: rijgt de 4 gevechten van één chakra aaneen (§1.2).
 *
 * Stroom: pad → confrontatie → gevecht → leermoment → (sub: volgende confrontatie
 * | eindbaas: meesterschap) → pad. HP blijft behouden tussen de 3 sub-bazen
 * (met +15 heal-bonus na elke sub-baas) en reset vol bij een nieuwe chakra.
 * Verlies → terug naar het pad (chakra opnieuw, geen verlies van unlocks).
 */
import { create } from 'zustand';
import type { Origin } from '../data/types';
import { HEADERS } from '../data/headers';
import { CHAKRAS } from '../data/chakras';
import type { Houding } from '../data/vn';
import { useBattleStore } from './battleStore';
import { useProgressStore } from './progressStore';

const HEAL_BONUS = 15;

export type RunFase = 'pad' | 'confrontatie' | 'gevecht' | 'leermoment' | 'meesterschap';

interface RunStore {
  fase: RunFase;
  chakra: Origin | null;
  baasIndex: number;
  spelerHp: number;
  startChakra: (c: Origin) => void;
  beginGevecht: (houding: Houding) => void;
  naGevecht: (res: 'gewonnen' | 'verloren') => void;
  naLeermoment: () => void;
  naMeesterschap: () => void;
  terugNaarPad: () => void;
}

export const useRunStore = create<RunStore>((set, get) => ({
  fase: 'pad',
  chakra: null,
  baasIndex: 0,
  spelerHp: 0,

  startChakra: (c) => set({ chakra: c, baasIndex: 0, spelerHp: HEADERS[c].maxHP, fase: 'confrontatie' }),

  beginGevecht: (houding) => {
    const { chakra, baasIndex, spelerHp } = get();
    if (chakra == null) return;
    useBattleStore.getState().start({
      chakra,
      baasIndex,
      houding,
      startHp: spelerHp,
      ontgrendeld: useProgressStore.getState().voltooideChakras,
    });
    set({ fase: 'gevecht' });
  },

  naGevecht: (res) => {
    if (res === 'verloren') {
      useBattleStore.getState().stop();
      set({ fase: 'pad', chakra: null });
      return;
    }
    // winst: HP overdragen voor de carry
    const battle = useBattleStore.getState().battle;
    set({ spelerHp: battle?.speler.hp ?? get().spelerHp, fase: 'leermoment' });
  },

  naLeermoment: () => {
    const { chakra, baasIndex, spelerHp } = get();
    if (chakra == null) return;
    const baas = CHAKRAS[chakra].bazen[baasIndex];
    useBattleStore.getState().stop();
    if (baas.eindbaas) {
      useProgressStore.getState().voltooiChakra(chakra);
      set({ fase: 'meesterschap' });
    } else {
      const maxHp = HEADERS[chakra].maxHP;
      set({ baasIndex: baasIndex + 1, spelerHp: Math.min(maxHp, spelerHp + HEAL_BONUS), fase: 'confrontatie' });
    }
  },

  naMeesterschap: () => set({ fase: 'pad', chakra: null }),

  terugNaarPad: () => {
    useBattleStore.getState().stop();
    set({ fase: 'pad', chakra: null });
  },
}));
